import { useState, useCallback, useRef } from 'react';

export function useMapSync(includeNoLocation = false) {
    const [mapBounds, setMapBounds] = useState(null);
    const [visibleMembers, setVisibleMembers] = useState([]);
    const [hoveredMember, setHoveredMember] = useState(null);
    const [highlightedMarker, setHighlightedMarker] = useState(null);
    
    const mapInstanceRef = useRef(null);
    const markersRef = useRef(new Map());
    const memberListRef = useRef(null);

    // Update visible members based on map bounds.
    // When includeNoLocation is true, members without a location are always shown
    // (appended after in-bounds members so located members appear first).
    const updateVisibleMembers = useCallback((members, bounds) => {
        if (!bounds || !members) {
            setVisibleMembers(members || []);
            return;
        }

        const inBounds = [];
        const noLocation = [];

        members.forEach(member => {
            if (!member.has_location || !member.latitude || !member.longitude) {
                if (includeNoLocation) {
                    noLocation.push(member);
                }
                return;
            }

            const lat = parseFloat(member.latitude);
            const lng = parseFloat(member.longitude);

            if (lat >= bounds.south &&
                lat <= bounds.north &&
                lng >= bounds.west &&
                lng <= bounds.east) {
                inBounds.push(member);
            }
        });

        setVisibleMembers([...inBounds, ...noLocation]);
    }, [includeNoLocation]);

    // Handle map bounds change
    const handleMapBoundsChange = useCallback((bounds, members) => {
        setMapBounds(bounds);
        updateVisibleMembers(members, bounds);
    }, [updateVisibleMembers]);

    // Handle member hover in list
    const handleMemberHover = useCallback((member) => {
        setHoveredMember(member);
        
        if (member && markersRef.current.has(member.user_id)) {
            const marker = markersRef.current.get(member.user_id);
            setHighlightedMarker(marker);
            
            // Add visual highlight to marker
            const markerElement = marker.getElement();
            if (markerElement) {
                // Find the inner member-marker div and add class to it
                const memberMarkerDiv = markerElement.querySelector('.member-marker');
                if (memberMarkerDiv) {
                    memberMarkerDiv.classList.add('marker-highlighted');
                }
            }
            
            // Open the popup for this marker, even if it's clustered
            if (marker.getPopup()) {
                // Try to open popup normally first
                marker.openPopup();
                
                // If marker is clustered and popup didn't open, create a temporary popup
                if (!marker.isPopupOpen() && mapInstanceRef.current && member.latitude && member.longitude) {
                    const tempPopup = new L.Popup({
                        closeButton: true,
                        autoClose: true,
                        closeOnEscapeKey: true,
                        className: 'temp-member-popup'
                    })
                    .setLatLng([parseFloat(member.latitude), parseFloat(member.longitude)])
                    .setContent(marker.getPopup().getContent())
                    .openOn(mapInstanceRef.current);
                }
            }
        } else {
            console.log('No marker found for member:', member.user_id);
        }
    }, []);

    // Handle member hover out in list
    const handleMemberHoverOut = useCallback(() => {
        if (hoveredMember && markersRef.current.has(hoveredMember.user_id)) {
            const marker = markersRef.current.get(hoveredMember.user_id);
            const markerElement = marker.getElement();
            if (markerElement) {
                // Find the inner member-marker div and remove class from it
                const memberMarkerDiv = markerElement.querySelector('.member-marker');
                if (memberMarkerDiv) {
                    memberMarkerDiv.classList.remove('marker-highlighted');
                }
            }
            
            // Close the popup for this marker
            if (marker.getPopup()) {
                marker.closePopup();
            }
        }
        
        // Also close any temporary popups that might be open
        if (mapInstanceRef.current) {
            mapInstanceRef.current.closePopup();
        }
        
        setHoveredMember(null);
        setHighlightedMarker(null);
    }, [hoveredMember]);

    // Handle marker hover on map
    const handleMarkerHover = useCallback((member) => {
        setHoveredMember(member);
        
        // Find the member list container
        let memberListElement = memberListRef.current;
        
        // Fallback: if ref is null, try to find the member list in DOM
        if (!memberListElement) {
            memberListElement = document.querySelector('.member-list-container');
        }
        
        if (memberListElement && member) {
            // Use the list's highlightMember API which pins member to top and highlights
            if (memberListElement.highlightMember) {
                memberListElement.highlightMember(member.user_id);
            }
        }
    }, []);

    // Handle marker hover out on map
    const handleMarkerHoverOut = useCallback(() => {
        
        // Find the member list container
        let memberListElement = memberListRef.current;
        
        // Fallback: if ref is null, try to find the member list in DOM
        if (!memberListElement) {
            memberListElement = document.querySelector('.member-list-container');
        }
        
        if (memberListElement) {
            // Use enhanced API if available
            if (memberListElement.clearHighlight) {
                memberListElement.clearHighlight();
            } else {
                // Fallback to DOM-based clearing (legacy)
                const highlightedElement = memberListElement.querySelector('.member-highlighted');
                if (highlightedElement) {
                    highlightedElement.classList.remove('member-highlighted');
                }
            }
        }
        
        // Reset states
        setHoveredMember(null);
        setHighlightedMarker(null);
    }, []);

    // Register map instance
    const registerMapInstance = useCallback((mapInstance) => {
        mapInstanceRef.current = mapInstance;
    }, []);

    // Register marker
    const registerMarker = useCallback((userId, marker) => {
        markersRef.current.set(userId, marker);
    }, []);

    // Unregister marker
    const unregisterMarker = useCallback((userId) => {
        markersRef.current.delete(userId);
    }, []);

    // Register member list container
    const registerMemberList = useCallback((listElement) => {
        memberListRef.current = listElement;
    }, []);

    return {
        mapBounds,
        visibleMembers,
        hoveredMember,
        highlightedMarker,
        handleMapBoundsChange,
        handleMemberHover,
        handleMemberHoverOut,
        handleMarkerHover,
        handleMarkerHoverOut,
        registerMapInstance,
        registerMarker,
        unregisterMarker,
        registerMemberList,
        updateVisibleMembers
    };
}
